/**
 * BrandingPreviewPanel — A3 Branding (Sprint Frontend Brief 5 · Tela 4).
 *
 * Preview ao vivo HONESTO dos campos reais: header com logo + accent das cores,
 * headline na fonte secundária, assinatura de email e (Regulated) header de
 * conformidade + footer de certificações. Reflete o estado do formulário em
 * memória (antes de salvar) — não simula telas inteiras do app.
 */

import { FONT_OPTIONS, type BrandingCertification, type SecondaryFont } from './types';

interface BrandingPreviewPanelProps {
  primaryColor: string;
  secondaryColor: string;
  secondaryFont: SecondaryFont | '';
  logoUrl: string | null;
  emailSignature: string;
  regulatedHeaderText: string;
  certifications: BrandingCertification[];
  showRegulated: boolean;
}

const FALLBACK_PRIMARY = '#D97706';
const FALLBACK_SECONDARY = '#78716C';
const FALLBACK_FONT = "'Geist', system-ui, sans-serif";

export function BrandingPreviewPanel({
  primaryColor,
  secondaryColor,
  secondaryFont,
  logoUrl,
  emailSignature,
  regulatedHeaderText,
  certifications,
  showRegulated,
}: BrandingPreviewPanelProps): JSX.Element {
  const primary = primaryColor !== '' ? primaryColor : FALLBACK_PRIMARY;
  const secondary = secondaryColor !== '' ? secondaryColor : FALLBACK_SECONDARY;
  const fontFamily = FONT_OPTIONS.find((f) => f.id === secondaryFont)?.family ?? FALLBACK_FONT;
  const signatureText = emailSignature.trim();

  return (
    <aside className="brand-preview" aria-label="Preview ao vivo do branding">
      <div className="brand-preview__head">
        <span className="brand-preview__title">Preview ao vivo</span>
        <span className="brand-preview__sub">reflete o formulário em edição</span>
      </div>
      <div className="brand-preview__body">
        {showRegulated && regulatedHeaderText.trim() !== '' && (
          <div className="brand-preview-tile">
            <span className="brand-preview-tile__label">Header de conformidade</span>
            <div className="brand-preview-regbar" style={{ borderColor: primary }}>
              {regulatedHeaderText}
            </div>
          </div>
        )}

        <div className="brand-preview-tile">
          <span className="brand-preview-tile__label">Header Lume Atendimento</span>
          <div className="brand-preview-header">
            <span className="brand-preview-header__logo" style={{ background: primary }}>
              {logoUrl !== null ? (
                <img src={logoUrl} alt="logo" className="brand-preview-header__logo-img" />
              ) : (
                'N'
              )}
            </span>
            <span className="brand-preview-header__name">NetComet</span>
          </div>
        </div>

        <div className="brand-preview-tile">
          <span className="brand-preview-tile__label">Accent das cores</span>
          <div className="brand-preview-btns">
            <span className="brand-preview-btn" style={{ background: primary, color: '#fff' }}>
              Primária
            </span>
            <span
              className="brand-preview-btn"
              style={{
                background: `${primary}1A`,
                color: primary,
                border: `1px solid ${primary}40`,
              }}
            >
              Secundária
            </span>
            <span
              className="brand-preview-btn"
              style={{ background: 'transparent', color: secondary }}
            >
              Ghost
            </span>
          </div>
        </div>

        <div className="brand-preview-tile">
          <span className="brand-preview-tile__label">Headline (fonte secundária)</span>
          <div className="brand-preview-headline" style={{ fontFamily }}>
            Excelência comunicacional configurável
          </div>
        </div>

        {signatureText !== '' && (
          <div className="brand-preview-tile">
            <span className="brand-preview-tile__label">Assinatura de email</span>
            <pre className="brand-preview-signature">{signatureText}</pre>
          </div>
        )}

        {showRegulated && certifications.length > 0 && (
          <div className="brand-preview-tile">
            <span className="brand-preview-tile__label">Footer com certificações</span>
            <div className="brand-preview-certs">
              {certifications.map((c) => (
                <span key={c.id} className="brand-preview-cert" title={c.label}>
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
